// ============================================================
// RentalContractFlow.jsx
// Drop into: ~/ivision-crm/frontend/src/RentalContractFlow.jsx
//
// A 4-step modal/flow launched from PublicRentalStorefront
// after a customer has added items to cart and clicked checkout.
//
// Steps:
//   1 — Who are you? (identity form)
//   2 — Review order (all details, equipment, totals)
//   3 — Sign the rental agreement (canvas signature pad)
//   4 — Pay deposit or full via Stripe / confirmation
//
// Props:
//   order       — {id, from_date, to_date, job_name, items, total_amount, ...}
//   onClose     — () => void
//   onComplete  — (signedOrder) => void
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { taxLines } from './lib/taxLines';
import { daysBetween, describeRateLine } from './lib/rentalDays';
import { fmtDate as fmtDateShared } from './lib/fmtDate';

const API = process.env.REACT_APP_API_URL || 'https://your-backend.railway.app';

// ── UTILS ─────────────────────────────────────────────────────

const fmtDate = (d) => fmtDateShared(d, { month: 'long', day: 'numeric', year: 'numeric' });
function fmtMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Day count + rate-line text come from lib/rentalDays.js — the same
// inclusive convention that priced the order. No private copy here.

// ── STEP INDICATOR ────────────────────────────────────────────

function StepBar({ step }) {
  const steps = ['Identity', 'Review', 'Sign', 'Payment'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 0 20px' }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const done    = step > n;
        const current = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#16a34a' : current ? '#F97316' : '#e5e7eb',
                color: done || current ? '#fff' : '#6b7280',
                fontWeight: 700, fontSize: 13,
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : n}
              </div>
              <span style={{ fontSize: 10, marginTop: 4, color: current ? '#F97316' : done ? '#16a34a' : '#9ca3af', fontWeight: current ? 700 : 400 }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 2, height: 2, background: step > n ? '#16a34a' : '#e5e7eb', marginBottom: 18, transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── STEP 1: IDENTITY FORM ─────────────────────────────────────

function StepIdentity({ data, onNext }) {
  const [errors, setErrors] = useState({});
  const refs = {
    signer_name:      useRef(),
    signer_email:     useRef(),
    signer_phone:     useRef(),
    signer_company:   useRef(),
    signer_address:   useRef(),
    signer_dl_number: useRef(),
  };

  function getValues() {
    return Object.fromEntries(Object.entries(refs).map(([k, r]) => [k, r.current?.value || '']));
  }

  function validate() {
    const vals = getValues();
    const e = {};
    if (!vals.signer_name?.trim())  e.signer_name  = 'Full name is required';
    if (!vals.signer_email?.trim()) e.signer_email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(vals.signer_email)) e.signer_email = 'Enter a valid email';
    if (!vals.signer_phone?.trim()) e.signer_phone = 'Phone is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (validate()) onNext(getValues());
  }

  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 };
  const inputStyle = (err) => ({
    padding: '9px 12px', borderRadius: 7,
    border: `1px solid ${err ? '#fca5a5' : '#e5e7eb'}`,
    fontSize: 14, width: '100%', boxSizing: 'border-box',
    background: err ? '#fff5f5' : '#fff',
    color: '#1e2535',
    outline: 'none',
  });

  const Field = ({ id, label, placeholder, type = 'text', required }) => (
    <div>
      <label style={labelStyle}>{label}{required && <span style={{ color: '#ef4444' }}>*</span>}</label>
      <input
        ref={refs[id]}
        type={type}
        defaultValue={data[id] || ''}
        placeholder={placeholder}
        style={inputStyle(errors[id])}
      />
      {errors[id] && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2, display: 'block' }}>{errors[id]}</span>}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Your Information</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>This information will appear on your rental agreement.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field id="signer_name" label="Full Legal Name" placeholder="John Smith" required />
        </div>
        <Field id="signer_email" label="Email Address" placeholder="john@example.com" type="email" required />
        <Field id="signer_phone" label="Phone Number" placeholder="(702) 555-0100" type="tel" required />
        <div style={{ gridColumn: '1 / -1' }}>
          <Field id="signer_company" label="Company / Business Name" placeholder="Smith Construction LLC" />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field id="signer_address" label="Address" placeholder="Street address, City, ST ZIP" />
        </div>
        <Field id="signer_dl_number" label="Driver's License / ID #" placeholder="Nevada DL: 1234567" />
        <div />
      </div>

      <div style={{ marginTop: 6, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, color: '#6b7280', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 16 }}>🔒</span>
        <span>Your information is used only for this rental agreement and is never shared or sold.</span>
      </div>

      <button onClick={next}
        style={{ marginTop: 20, width: '100%', padding: '12px 0', borderRadius: 8, border: 'none', background: '#F97316', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
        Review My Order →
      </button>
    </div>
  );
}

// ── STEP 2: ORDER REVIEW ──────────────────────────────────────

function StepReview({ order, identity, onNext, onBack }) {
  const items  = order.items || [];
  // Every figure is the order's stored snapshot (server lib/totals.js) —
  // nothing is recomputed in the browser, and the deposit is the amount
  // the server stamped from the tenant's rule, not a hardcoded 25%.
  const total    = Number(order.total || 0);
  const deposit  = Number(order.deposit_amount || 0);
  const rows     = taxLines(order, { includeTotal: false });
  const days     = daysBetween(order.from_date, order.to_date);

  // Explicit colors throughout: the host page (storefront) is dark-themed
  // and sets a light text color that this white card would otherwise
  // inherit, leaving subtotal/tax/total nearly invisible.
  const rowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: '1px solid #f3f4f6', color: '#0f172a' };
  const labelStyle = { color: '#374151' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Review Your Rental</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Confirm all details before signing.</p>
      </div>

      {/* Who's renting */}
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 14, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Renter</div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{identity.signer_name}</div>
        {identity.signer_company && <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{identity.signer_company}</div>}
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{identity.signer_email} · {identity.signer_phone}</div>
        {identity.signer_address && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>📍 {identity.signer_address}</div>}
      </div>

      {/* Rental period */}
      <div style={{ background: '#fff7ed', borderRadius: 10, padding: '14px 16px', marginBottom: 14, border: '1px solid #fed7aa' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Rental Period</div>
        <div style={{ display: 'flex', gap: 24 }}>
          <div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>START</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{fmtDate(order.from_date)}</div>
          </div>
          <div style={{ color: '#F97316', fontSize: 20, alignSelf: 'center' }}>→</div>
          <div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>END</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{fmtDate(order.to_date)}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>DURATION</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#F97316' }}>{days} day{days !== 1 ? 's' : ''}</div>
          </div>
        </div>
        {order.job_name && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>📋 Job: <strong>{order.job_name}</strong></div>
        )}
        {(order.delivery_street || order.delivery_city) && (
          <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>🚧 Site: {[order.delivery_street, order.delivery_city, order.delivery_state].filter(Boolean).join(', ')}</div>
        )}
      </div>

      {/* Equipment */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 14, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Equipment</div>
        {items.map((li, i) => (
          <div key={i} style={rowStyle}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{li.item_name}</span>
              <span style={{ fontSize: 12, color: '#4b5563', marginLeft: 8 }}>{describeRateLine(li, days)}</span>
            </div>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{fmtMoney(li.subtotal)}</span>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
        {rows.map(r => [r.label, r.kind === 'discount' ? `−${fmtMoney(-r.value)}` : fmtMoney(r.value)]).map(([lbl, val]) => (
          <div key={lbl} style={{ ...rowStyle }}>
            <span style={labelStyle}>{lbl}</span><span style={{ color: '#0f172a', fontWeight: 600 }}>{val}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 4px', borderTop: '2px solid #0f172a', marginTop: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Total Due</span>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{fmtMoney(total)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', background: '#eff6ff', borderRadius: 6, padding: '8px 10px', marginTop: 8 }}>
          <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>🔒 Deposit to pay now</span>
          <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>{fmtMoney(deposit)}</span>
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'right' }}>
          Balance of {fmtMoney(total - deposit)} due on equipment return
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button onClick={onBack} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          ← Back
        </button>
        <button onClick={onNext} style={{ flex: 2, padding: '11px 0', borderRadius: 8, border: 'none', background: '#F97316', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
          Looks Good — Sign Agreement →
        </button>
      </div>
    </div>
  );
}

// ── STEP 3: SIGNATURE PAD ─────────────────────────────────────

function StepSign({ order, identity, onNext, onBack, submitting, termsDocs = [] }) {
  const canvasRef    = useRef(null);
  const drawing      = useRef(false);
  const lastPos      = useRef(null);
  const [signed, setSigned]   = useState(false);
  const [agreed, setAgreed]   = useState(false);
  const [error, setError]     = useState('');
  const pdfDocs = termsDocs.filter(td => td.format === 'pdf_upload');
  const [pdfAcks, setPdfAcks] = useState({}); // { [docId]: bool } — one required ack per pdf_upload doc
  const allPdfsAcked = pdfDocs.every(td => pdfAcks[td.id]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FAFAFA';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  function startDraw(e) {
    e.preventDefault();
    drawing.current = true;
    const pos = getPos(e, canvasRef.current);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
  }

  function draw(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setSigned(true);
  }

  function stopDraw(e) {
    e.preventDefault();
    drawing.current = false;
  }

  function clearSig() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FAFAFA';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
  }

  function submit() {
    if (!signed) { setError('Please draw your signature above.'); return; }
    if (!agreed) { setError('Please agree to the terms before signing.'); return; }
    if (!allPdfsAcked) { setError('Please confirm you have read each attached document before signing.'); return; }
    setError('');
    const sigData = canvasRef.current.toDataURL('image/png');
    onNext(sigData);
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Sign Rental Agreement</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Draw your signature in the box below.</p>
      </div>

      {/* Mini contract summary */}
      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#374151', border: '1px solid #e2e8f0' }}>
        <strong>{identity.signer_name}</strong>{identity.signer_company ? ` · ${identity.signer_company}` : ''} agrees to rent the listed equipment from <strong>{fmtDate(order.from_date)}</strong> through <strong>{fmtDate(order.to_date)}</strong> for a total of <strong>{fmtMoney(order.total_amount)}</strong>, subject to the terms below.
      </div>

      {/* Terms — the tenant's real, current, owner-edited terms */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, maxHeight: 220, overflowY: 'auto', fontSize: 11, color: '#374151', lineHeight: 1.6, marginBottom: 14 }}>
        {termsDocs.length === 0 && <span style={{ color: '#94a3b8' }}>Loading terms…</span>}
        {termsDocs.map((td, i) => (
          <div key={i} style={{ marginBottom: i < termsDocs.length - 1 ? 16 : 0 }}>
            <strong style={{ fontSize: 12, color: '#0f172a' }}>{td.title}</strong><br /><br />
            {td.format === 'pdf_upload' ? (
              <>
                The complete {td.title} (Version {td.version}) is a separate document.{' '}
                <a href={td.file_url} target="_blank" rel="noreferrer" style={{ color: '#F97316', fontWeight: 700 }}>
                  Open and review it here before signing →
                </a>
              </>
            ) : (
              <span style={{ whiteSpace: 'pre-wrap' }}>{td.renderedBody}</span>
            )}
          </div>
        ))}
      </div>

      {/* One required ack per attached PDF document — the signature pad
          below is disabled until every one of these is checked */}
      {pdfDocs.map(td => (
        <label key={td.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '10px 14px', background: pdfAcks[td.id] ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: `1px solid ${pdfAcks[td.id] ? '#bbf7d0' : '#e2e8f0'}`, marginBottom: 10 }}>
          <input type="checkbox" checked={!!pdfAcks[td.id]} onChange={e => setPdfAcks(p => ({ ...p, [td.id]: e.target.checked }))} style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }} />
          <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
            I have read and agree to <strong>{td.title}</strong> (Version {td.version}).
          </span>
        </label>
      ))}

      {/* Signature canvas — disabled until every attached PDF is acked */}
      <div style={{ position: 'relative', marginBottom: 6 }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={140}
          onMouseDown={allPdfsAcked ? startDraw : undefined}
          onMouseMove={allPdfsAcked ? draw : undefined}
          onMouseUp={allPdfsAcked ? stopDraw : undefined}
          onMouseLeave={allPdfsAcked ? stopDraw : undefined}
          onTouchStart={allPdfsAcked ? startDraw : undefined}
          onTouchMove={allPdfsAcked ? draw : undefined}
          onTouchEnd={allPdfsAcked ? stopDraw : undefined}
          style={{
            width: '100%', height: 140,
            border: `2px dashed ${signed ? '#0f172a' : '#cbd5e1'}`,
            borderRadius: 8, cursor: allPdfsAcked ? 'crosshair' : 'not-allowed',
            background: allPdfsAcked ? '#FAFAFA' : '#f1f5f9', display: 'block',
            touchAction: 'none', opacity: allPdfsAcked ? 1 : 0.6,
          }}
        />
        {!signed && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', textAlign: 'center' }}>
            <div style={{ fontSize: 28, color: '#cbd5e1' }}>✍️</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{allPdfsAcked ? 'Draw your signature here' : 'Confirm the document(s) above first'}</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>Signing as: <strong style={{ color: '#374151' }}>{identity.signer_name}</strong></span>
        <button onClick={clearSig} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', color: '#6b7280' }}>
          Clear
        </button>
      </div>

      {/* Agree checkbox */}
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '10px 14px', background: agreed ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: `1px solid ${agreed ? '#bbf7d0' : '#e2e8f0'}`, marginBottom: 14 }}>
        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }} />
        <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
          I, <strong>{identity.signer_name}</strong>, have read and agree to all terms and conditions of this Equipment Rental Agreement. I understand this constitutes a legally binding electronic signature.
        </span>
      </label>

      {error && (
        <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#dc2626', marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onBack} disabled={submitting}
          style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          ← Back
        </button>
        <button onClick={submit} disabled={submitting || !signed || !agreed || !allPdfsAcked}
          style={{
            flex: 2, padding: '11px 0', borderRadius: 8, border: 'none',
            background: (!signed || !agreed || !allPdfsAcked || submitting) ? '#d1d5db' : '#16a34a',
            color: '#fff', fontWeight: 700, fontSize: 15,
            cursor: (!signed || !agreed || !allPdfsAcked || submitting) ? 'not-allowed' : 'pointer',
          }}>
          {submitting ? '⏳ Saving…' : '✓ Sign & Proceed to Payment →'}
        </button>
      </div>
    </div>
  );
}

// ── STEP 4: PAYMENT ───────────────────────────────────────────

function StepPayment({ order, identity, contractBase64, onClose, onComplete, apiBase }) {
  const BASE = apiBase || `${API}/api/rentals`;
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const items    = order.items || [];
  const total    = Number(order.total || 0);
  const deposit  = Number(order.deposit_amount || 0);
  const payRows  = taxLines(order, { includeTotal: false });
  const payDays  = daysBetween(order.from_date, order.to_date);

  function downloadContract() {
    if (!contractBase64) return;
    const link  = document.createElement('a');
    link.href   = `data:application/pdf;base64,${contractBase64}`;
    link.download = `rental-agreement-order-${order.id}.pdf`;
    link.click();
  }

  async function checkout(type) {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_token:    order.public_token,
          payment_type:   type,
          customer_email: identity.signer_email,
          customer_name:  identity.signer_name,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError((data.error || data.message || JSON.stringify(data) || 'Could not start checkout. Please try again.'));
      }
    } catch (e) {
      setError('Payment service unavailable. Please call us to complete your booking.');
    }
    setLoading(false);
  }

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4', border: '2px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 28 }}>
          ✓
        </div>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Agreement Signed!</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
          Contract emailed to <strong>{identity.signer_email}</strong>
        </p>
      </div>

      {/* Download link */}
      {contractBase64 && (
        <button onClick={downloadContract}
          style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          📄 Download Signed Contract (PDF)
        </button>
      )}

      {/* Order summary */}
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Order Summary</div>
        {items.map((li, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
            <span>{li.item_name} <span style={{ color: '#6b7280', fontSize: 12 }}>· {describeRateLine(li, payDays)}</span></span>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>{fmtMoney(li.subtotal)}</span>
          </div>
        ))}
        {payRows.map(r => (
          <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', color: '#374151' }}>
            <span>{r.label}</span><span>{r.kind === 'discount' ? `−${fmtMoney(-r.value)}` : fmtMoney(r.value)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, marginTop: 10, paddingTop: 8, borderTop: '2px solid #0f172a', color: '#0f172a' }}>
          <span>Total</span>
          <span>{fmtMoney(total)}</span>
        </div>
      </div>

      {/* Payment options */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Choose Payment</div>

      <button onClick={() => checkout('deposit')} disabled={loading}
        style={{
          width: '100%', padding: '14px 16px', borderRadius: 10, marginBottom: 10,
          border: '2px solid #F97316', background: loading ? '#fde8d8' : '#fff7ed',
          cursor: loading ? 'not-allowed' : 'pointer', textAlign: 'left',
          transition: 'all 0.15s',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#F97316' }}>Pay Deposit — {fmtMoney(deposit)}</div>
            <div style={{ fontSize: 12, color: '#9a3412', marginTop: 2 }}>Balance of {fmtMoney(total - deposit)} due on equipment return</div>
          </div>
          <span style={{ fontSize: 20 }}>→</span>
        </div>
      </button>

      <button onClick={() => checkout('full')} disabled={loading}
        style={{
          width: '100%', padding: '14px 16px', borderRadius: 10, marginBottom: 10,
          border: '2px solid #2563eb', background: loading ? '#dbeafe' : '#eff6ff',
          cursor: loading ? 'not-allowed' : 'pointer', textAlign: 'left',
          transition: 'all 0.15s',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#2563eb' }}>Pay in Full — {fmtMoney(total)}</div>
            <div style={{ fontSize: 12, color: '#1e40af', marginTop: 2 }}>Nothing owed on return</div>
          </div>
          <span style={{ fontSize: 20 }}>→</span>
        </div>
      </button>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626', marginBottom: 10 }}>
          {error}
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', margin: '12px 0 0' }}>
        🔒 Secure checkout powered by Stripe. Your card is never stored on our servers.
      </p>
    </div>
  );
}

// ── MAIN FLOW COMPONENT ───────────────────────────────────────

export default function RentalContractFlow({ order, onClose, onComplete, apiBase, prefill, holdNotice }) {
  const BASE = apiBase || `${API}/api/rentals`;
  const [step,      setStep]      = useState(1);
  const [identity,  setIdentity]  = useState({
    signer_name:    prefill?.signer_name    || '',
    signer_email:   prefill?.signer_email   || '',
    signer_phone:   prefill?.signer_phone   || '',
    signer_company: '',
    signer_address: '',
    signer_dl_number: '',
  });
  const [submitting,   setSubmitting]   = useState(false);
  const [contractB64,  setContractB64]  = useState(null);
  const [termsDocs,    setTermsDocs]    = useState([]);
  const [businessName, setBusinessName] = useState('');

  const loadTerms = useCallback(() => {
    return fetch(`${BASE}/o/${order.public_token}/terms-preview`)
      .then(r => r.json())
      .then(d => { setTermsDocs(d.terms || []); setBusinessName(d.business_name || ''); })
      .catch(() => {});
  }, [BASE, order.public_token]);

  useEffect(() => { loadTerms(); }, [loadTerms]);

  function updateIdentity(key, val) {
    setIdentity(prev => ({ ...prev, [key]: val }));
  }

  async function handleSign(signatureData) {
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/o/${order.public_token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...identity, signature_data: signatureData, contract_terms_ids: termsDocs.map(td => td.id) }),
      });
      const data = await res.json();
      if (data.success) {
        setContractB64(data.contract_pdf_base64 || null);
        setStep(4);
        // onComplete fires after payment, not here
      } else if (data.error === 'terms_changed') {
        alert(data.message || 'The rental terms were updated. Please review them again before signing.');
        await loadTerms();
        setStep(2); // back to review, now with the refreshed terms/ids
      } else {
        alert(data.message || data.error || 'Could not save signature. Please try again.');
      }
    } catch (e) {
      console.error('Sign error:', e);
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '20px 16px', overflowY: 'auto',
    }}
      onClick={e => { /* disabled: clicking outside no longer closes modal */ }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        animation: 'slideUp 0.2s ease',
        marginTop: 20,
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px 0', borderBottom: '1px solid #f3f4f6', paddingBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>🚧</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{businessName || 'Equipment Rentals'}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Rental Agreement & Payment</div>
              </div>
            </div>
            {step < 4 && (
              <button onClick={onClose}
                style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            )}
          </div>
          <StepBar step={step} />
          {holdNotice && step < 4 && (
            <div data-testid="hold-notice" style={{ marginTop: 12, padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12, color: '#9a3412' }}>
              ⏱ {holdNotice}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px 24px' }}>
          {step === 1 && (
            <StepIdentity
              data={identity}
              onNext={(vals) => { setIdentity(vals); setStep(2); }}
            />
          )}
          {step === 2 && (
            <StepReview
              order={order}
              identity={identity}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <StepSign
              order={order}
              identity={identity}
              onNext={handleSign}
              onBack={() => setStep(2)}
              submitting={submitting}
              termsDocs={termsDocs}
            />
          )}
          {step === 4 && (
            <StepPayment
              order={order}
              identity={identity}
              contractBase64={contractB64}
              onClose={onClose}
              onComplete={onComplete}
              apiBase={BASE}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
